import { useSessionAlerts } from '@/hooks/useSessionAlerts';
import { cn } from '@/lib/utils';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Bell, ExternalLink, MessageSquare, Smile, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';


interface NotificationBellProps {
    collapsed?: boolean; // la sidebar peut être réduite
  }

const NotificationBell = ({ collapsed = false}: NotificationBellProps) => {
    const [open, setOpen] = useState(false);
    const { alerts, count, isLoading } = useSessionAlerts();
    const navigate = useNavigate();

    
    return (
        <div className="relative">

             {/* ── Le bouton cloche ── */}
             <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                title="Alertes"
                className={cn(
                    "flex w-full items-center rounded-lg px-3 py-2.5",
                "text-white/90 transition-colors duration-200 hover:bg-white/10",
                collapsed ? "justify-center gap-0 lg:px-2" : "gap-3"
                        )}
                    >
                    {/* Conteneur relatif pour positionner le badge sur la cloche */}
                    <div className="relative shrink-0">
                    <Bell className="h-5 w-5" />
                    {count > 0 && (
                        <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                        {count > 9 ? "9+" : count}
                        </span>
                    )}
                    </div>
                    <span className={cn(collapsed && "lg:hidden")}>Alertes</span>

             </button>

            {/* ── Le panel déroulant ── */}
            {open && (
                <>
                
                     {/* Fond transparent cliquable pour fermer le panel */}
                    <div
                        className="fixed inset-0 z-10"
                        onClick={() => setOpen(false)}
                    />
                    {/* Le panel déroulant */}
                    <div className="absolute bottom-0 left-full z-20 mb-2 ml-2 w-80 rounded-xl border border-gray-200 bg-white shadow-xl">
                            {/* En-tête du panel */}
                            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                                <div className="flex items-center gap-2">
                                    <Bell className="h-4 w-4 text-gray-600" />
                                    <h3 className="text-sm font-semibold text-gray-800">
                                    Sessions à vérifier
                                    </h3>
                                </div>
                                {/* Bouton pour fermer */}
                                <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>    

                            {/* Corps : liste des alertes */}
                            <div className="max-h-80 overflow-y-auto">
                            {isLoading && (
                                <p className="p-4 text-center text-sm text-gray-500">
                                Chargement...
                                </p>
                            )}


                            {!isLoading && alerts.length === 0 && (
                                <p className="p-4 text-center text-sm text-gray-500">
                                Aucune alerte <Smile className="h-3.5 w-3.5 inline-block ml-1" />
                                </p>
                            )}

                                {!isLoading && alerts.slice(0, 2).map((session) => (
                                    <div
                                    key={session.id}
                                    className="border-b border-gray-50 px-4 py-3 last:border-b-0"
                                    >
                                    {/* Nom de l'employé */}
                                    <div className="flex items-start justify-between gap-2">
                                        <p className="text-sm font-medium text-gray-800">
                                        {session.user?.firstName} {session.user?.lastName}
                                        </p>
                                        {/* Lien vers la page sessions */}
                                       
                                    </div>
                                    {/* Date de la session */}
                                    <p className="mt-0.5 text-xs text-gray-500">
                                        {format(parseISO(session.startTime), "d MMM yyyy", { locale: fr })}
                                    </p>
                                    {/* Le commentaire */}
                                    <div className="mt-1.5 flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1.5">
                                        <MessageSquare className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />
                                        <p className="text-xs text-amber-800">{session.comment}</p>
                                    </div>
                                    </div>
                                ))}
                            </div>
                            {/* Pied du panel : lien vers la page sessions */}
                            {alerts.length > 0 ? (
                                <div className="border-t border-gray-100 px-4 py-2 flex items-center justify-between">
                                    {alerts.length > 5 && (
                                    <span className="text-xs text-gray-400">
                                        +{alerts.length - 5} autres
                                    </span>
                                    )}
                                    
                                    <button
                                    onClick={() => {
                                        navigate("/admin/alerts");
                                        setOpen(false);
                                    }}
                                    className="text-xs cursor-pointer font-medium text-primary hover:underline ml-auto"
                                    >
                                    Voir toutes les alertes <ExternalLink className="h-3.5 w-3.5 inline-block ml-1" />
                                    </button>
                                </div>)
                                : (
                                  <div className="border-t border-gray-100 px-4 py-2 flex items-center justify-between">    
                                    <button
                                    onClick={() => {
                                        navigate("/admin/alerts");
                                        setOpen(false);
                                    }}
                                    className="text-xs cursor-pointer font-medium text-primary hover:underline ml-auto"
                                    >
                                    Voir les alertes traitées <ExternalLink className="h-3.5 w-3.5 inline-block ml-1" />
                                    </button>
                                </div>
                                )}
                        </div>
                </>
            )}           
        </div>
    );
};

export default NotificationBell;